import React, { useRef } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Table } from "reactstrap";
import { ProjectListTableRowSkeleton } from "objs/_ConfiguratorObjs/ProjectList/SubComponents/ProjectListTable/ProjectListTableRowSkeleton";

type VirtualizedTableColumn = {
  label: React.ReactNode;
  width?: React.CSSProperties["width"];
  dataTestid?: string;
};

type VirtualizedTableProps<TItem> = {
  columns: VirtualizedTableColumn[];
  list: TItem[];
  renderRow: (item: TItem) => React.ReactNode;
  renderNoData: () => React.ReactNode;
  isLoading: boolean;
  skeletonCount: number;
  rowHeight?: number;
  overscan?: number;
  accessId?: (item: TItem) => string | number;
};

export const VirtualizedTable = <TItem,>({
  columns,
  list,
  renderRow,
  renderNoData,
  isLoading,
  skeletonCount,
  rowHeight = 68,
  overscan = 7,
  accessId,
}: VirtualizedTableProps<TItem>) => {
  const listRef = useRef<HTMLTableSectionElement | null>(null);

  // eslint-disable-next-line react-hooks/refs
  const rowVirtualizer = useWindowVirtualizer({
    count: !isLoading ? list.length : skeletonCount,
    // eslint-disable-next-line react-hooks/refs
    scrollMargin: listRef.current?.offsetTop ?? 0,
    estimateSize: () => rowHeight,
    overscan,
  });

  return (
    <Table className="mb-4">
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th
              key={index}
              style={{ width: column.width }}
              data-testid={column.dataTestid}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody
        ref={listRef}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().length > 0
          ? rowVirtualizer.getVirtualItems().map((virtualItem, index) => (
              <tr
                key={
                  accessId && list[virtualItem.index]
                    ? accessId(list[virtualItem.index])
                    : index
                }
                style={{
                  position: "absolute",
                  display: "flex",
                  width: "100%",
                  height: `${rowHeight}px`,
                  transform: `translateY(${virtualItem.start - rowVirtualizer.options.scrollMargin}px)`,
                }}
              >
                {isLoading ? (
                  <ProjectListTableRowSkeleton />
                ) : (
                  renderRow(list[virtualItem.index])
                )}
              </tr>
            ))
          : renderNoData()}
      </tbody>
    </Table>
  );
};
